/**
 * Store SKUs on the product sync screens (Phase 88, 88a).
 *
 * A composition org with its store SKU switch on keeps each standard
 * product's live store SKU as its print SKU instead of minting one. The SERVER
 * decides that mode (`storeSkuMode` on the preview response) and classifies
 * every product (`storeSku`), so nothing here parses a SKU or guesses a mode.
 * This module only holds the pure glue the page, the rows, the job review and
 * the worklist share:
 *
 * - the declared run scope (`buildDeclaredScope`), which the existing
 *   `startProductSyncJob` mutation takes as is;
 * - which run cluster the action bar shows (`storeSkuRunMode`) and why the
 *   one Set up button is disabled (`storeSkuRunDisabledReason`);
 * - the store SKU filter values, their URL parser (`parseStoreSkuFilter`) and
 *   the filtered Products list link (`storeSkuProductsHref`);
 * - the Store SKUs settings deep link (`STORE_SKU_SETTINGS_HREF`);
 * - plain copy for every reason the backend can name
 *   (`DECLARED_REASON_KEYS`), typed over the backend's own unions so a new
 *   literal fails typecheck instead of reaching an operator raw.
 */
import { MAX_PRODUCTS_PER_JOB } from '@threadheads/shared';
import type { RouterInputs } from '@/lib/trpc';
// Type-only, like `@/lib/trpc` itself: the job `error` column is a plain
// string on the wire, so its literals are read from where they are declared.
import type { BuildSyncPlanFailureReason } from '../../../../../backend/src/modules/pod-product-sync/apply/plan';
import type { DeclaredJobFailure, StoreSkuExecuteFailure } from '../../../../../backend/src/modules/pod-product-sync/apply/declare-job';
import type { PlanBlocker } from './apply-types';
import type { PreviewProduct } from './types';

// ---------------------------------------------------------------------------
// Router-inferred shapes.
// ---------------------------------------------------------------------------

/** The server's per-product store SKU summary (88-05). Present only in store SKU mode. */
export type StoreSkuSummary = NonNullable<PreviewProduct['storeSku']>;
export type StoreSkuStatus = StoreSkuSummary['status'];
export type StoreSkuBlockingReason = NonNullable<StoreSkuSummary['blockingReason']>;

/** The Products list filter in store SKU mode: a server query input, never a browser filter. */
export type StoreSkuFilter = NonNullable<RouterInputs['productSync']['getProductSyncPreview']['storeSkuFilter']>;

export type ProductSyncJobScope = RouterInputs['productSync']['startProductSyncJob']['scope'];
export type RunOptions = ProductSyncJobScope['options'];

/** Why the declare core left a variant out (every outcome except `declare`). */
export type DeclaredSkipOutcome = Exclude<NonNullable<PlanBlocker['skippedVariants']>[number]['outcome'], 'declare'>;
/** The one declared plan blocker. */
export type DeclaredBlockerReason = Extract<PlanBlocker['reason'], 'declared_nothing_to_set_up'>;
/** Every literal a failed job can carry in its `error` column. */
export type JobFailureReason = BuildSyncPlanFailureReason | DeclaredJobFailure | StoreSkuExecuteFailure;

/** Every reason a declared plan, a declared row or a set-up job can name. */
export type DeclaredReason = DeclaredSkipOutcome | StoreSkuBlockingReason | DeclaredBlockerReason | JobFailureReason;

// ---------------------------------------------------------------------------
// Settings link.
// ---------------------------------------------------------------------------

/** The Store SKUs section in Production settings (88-08 gives it this id). */
export const STORE_SKU_SETTINGS_HREF = '/production/settings?area=printing#store-skus';

// ---------------------------------------------------------------------------
// The declared scope.
// ---------------------------------------------------------------------------

export type BuildDeclaredScopeResult =
  | { ok: true; scope: ProductSyncJobScope }
  | { ok: false; error: { key: 'bulkBar.overCap'; values: { max: number } } };

/**
 * The scope the existing `startProductSyncJob` mutation takes for a store SKU
 * set-up: product ids only, no design code and no files. Over the job cap it
 * returns the same over-cap copy the Sync button uses. The cap is the shared
 * `MAX_PRODUCTS_PER_JOB` the backend's job schema enforces.
 */
export function buildDeclaredScope(productIds: readonly string[], options: RunOptions): BuildDeclaredScopeResult {
  if (productIds.length > MAX_PRODUCTS_PER_JOB) {
    return { ok: false, error: { key: 'bulkBar.overCap', values: { max: MAX_PRODUCTS_PER_JOB } } };
  }
  return {
    ok: true,
    scope: {
      identity: 'declared',
      products: productIds.map((productId) => ({ productId })),
      options,
    },
  };
}

// ---------------------------------------------------------------------------
// The run cluster.
// ---------------------------------------------------------------------------

/**
 * `store_skus`: ONE Set up button replaces the overwrite select and Sync.
 * `store_skus_and_sync` (86-23): a store SKU org in BYO mode. Sync (new
 *   products only, never overwriting) sits next to Set up (store SKU
 *   products only); no overwrite select.
 * `none`: store SKU mode for a non-admin, who sees statuses only.
 * `sync`: today's cluster, unchanged.
 */
export type StoreSkuRunMode = 'store_skus' | 'store_skus_and_sync' | 'none' | 'sync';

export function storeSkuRunMode({
  storeSkuMode,
  admin,
  storeSkuByo = false,
}: {
  storeSkuMode: boolean;
  admin: boolean;
  /** The preview's `storeSkuByo` (absent: false). */
  storeSkuByo?: boolean;
}): StoreSkuRunMode {
  if (!storeSkuMode) return 'sync';
  if (!admin) return 'none';
  return storeSkuByo ? 'store_skus_and_sync' : 'store_skus';
}

/**
 * 86-23: the selected products a Sync run may take. In store SKU mode only
 * the new ones (`storeSkuNew`); everywhere else every selected product, as
 * today.
 */
export function syncCandidates<T extends { storeSkuNew?: true }>(products: Iterable<T>, storeSkuMode: boolean): T[] {
  const out: T[] = [];
  for (const p of products) if (!storeSkuMode || p.storeSkuNew === true) out.push(p);
  return out;
}

/**
 * A product Set up would act on: at least one variant the declare core would
 * declare (`storeSku.settable`, the server's own count). A product that is
 * only partial (every remaining size refused) or only changed in the store
 * has nothing Set up can do, so it does not count. A changed product with a
 * new size does.
 */
export function storeSkuNeedsSetUp(settable: number | null | undefined): boolean {
  return (settable ?? 0) > 0;
}

export interface DisabledReason {
  key: string;
  values?: Record<string, number>;
}

/**
 * Why the Set up button is disabled, first match wins, or null when it can
 * run. A dirty buffer blocks it because the set-up resolves against the SAVED
 * code tables.
 */
export function storeSkuRunDisabledReason({
  selected,
  needSetUp,
  bufferDirty,
  max,
  allNew = false,
}: {
  selected: number;
  needSetUp: number;
  bufferDirty: boolean;
  max: number;
  /** 86-23: every selected product is a new product (made by Sync, never Set up). */
  allNew?: boolean;
}): DisabledReason | null {
  if (selected === 0) return { key: 'bulkBar.needSelectionSetUp' };
  if (needSetUp === 0) return { key: allNew ? 'bulkBar.storeSkuOnlyNewProducts' : 'bulkBar.storeSkuNothingToSetUp' };
  if (bufferDirty) return { key: 'actions.previewRunDirty' };
  if (needSetUp > max) return { key: 'bulkBar.overCap', values: { max } };
  return null;
}

// ---------------------------------------------------------------------------
// The store SKU filter.
// ---------------------------------------------------------------------------

/** Label key per filter value, in menu order. Exhaustive over the router's enum. */
export const STORE_SKU_FILTER_LABEL_KEYS: Record<StoreSkuFilter, string> = {
  all: 'statusFilter.storeSku.all',
  needed: 'statusFilter.storeSku.needed',
  partial: 'statusFilter.storeSku.partial',
  set_up: 'statusFilter.storeSku.set_up',
  changed: 'statusFilter.storeSku.changed',
  new: 'statusFilter.storeSku.new',
};

export const STORE_SKU_FILTERS = Object.keys(STORE_SKU_FILTER_LABEL_KEYS) as StoreSkuFilter[];

/** The Products list's search param for the store SKU filter. */
export const STORE_SKU_PARAM = 'storeSku';

/** `?storeSku=` to a filter value; anything unknown (or absent) is `all`. */
export function parseStoreSkuFilter(value: string | null | undefined): StoreSkuFilter {
  return typeof value === 'string' && (STORE_SKU_FILTERS as string[]).includes(value) ? (value as StoreSkuFilter) : 'all';
}

/** The Products list, filtered to one store SKU status (`all` carries no filter). */
export function storeSkuProductsHref(filter: StoreSkuFilter): string {
  const base = '/product-sync?tab=products';
  return filter === 'all' ? base : `${base}&${STORE_SKU_PARAM}=${filter}`;
}

// ---------------------------------------------------------------------------
// Reason copy.
// ---------------------------------------------------------------------------

/** Why a variant was not set up, or why a whole product has nothing to set up. */
const VARIANT_REASON_KEYS: Record<DeclaredSkipOutcome | StoreSkuBlockingReason | DeclaredBlockerReason, string> = {
  already_declared_same: 'storeSkuReason.already_declared_same',
  declared_sku_changed: 'storeSkuReason.declared_sku_changed',
  existing_derived_row: 'storeSkuReason.existing_derived_row',
  pap_product: 'storeSkuReason.pap_product',
  excluded: 'storeSkuReason.excluded',
  not_active: 'storeSkuReason.not_active',
  conflicting_duplicate: 'storeSkuReason.conflicting_duplicate',
  unmapped_type: 'storeSkuReason.unmapped_type',
  unmapped_size: 'storeSkuReason.unmapped_size',
  type_mismatch: 'storeSkuReason.type_mismatch',
  size_mismatch: 'storeSkuReason.size_mismatch',
  no_size: 'storeSkuReason.no_size',
  invalid_code_format: 'storeSkuReason.invalid_code_format',
  custom_sku: 'storeSkuReason.custom_sku',
  empty_sku: 'storeSkuReason.empty_sku',
  unparseable_sku: 'storeSkuReason.unparseable_sku',
  declared_nothing_to_set_up: 'job.blocker.declared_nothing_to_set_up',
  left_for_product_sync: 'storeSkuReason.left_for_product_sync',
};

/** Why a job failed as a whole: plain sentences in place of the stored literal. */
const JOB_FAILURE_KEYS: Record<JobFailureReason, string> = {
  missing_custom_print_sku_mapping: 'job.error.missing_custom_print_sku_mapping',
  no_products_in_scope: 'job.error.no_products_in_scope',
  store_skus_not_supported: 'job.error.store_skus_not_supported',
  declared_sku_mode_off: 'job.error.declared_sku_mode_off',
  org_uses_store_skus: 'job.error.org_uses_store_skus',
  declared_check_failed: 'job.error.declared_check_failed',
  store_sku_overwrite_not_allowed: 'job.error.store_sku_overwrite_not_allowed',
  old_product_check_failed: 'job.error.old_product_check_failed',
  // 86-24: a store SKU org's execution re-checks and its store write guard.
  plan_stale_old_product: 'job.error.plan_stale_old_product',
  plan_stale_sku_in_use: 'job.error.plan_stale_sku_in_use',
  plan_stale_design_in_use: 'job.error.plan_stale_design_in_use',
  plan_stale_metafield_changed: 'job.error.plan_stale_metafield_changed',
  plan_recheck_failed: 'job.error.plan_recheck_failed',
  store_sku_live_store_outside_production: 'job.error.store_sku_live_store_outside_production',
  mixed_identity_plan: 'job.error.mixed_identity_plan',
  declared_write_failed: 'job.error.declared_write_failed',
};

/** Every reason the backend can name, to its `productSync` key. */
export const DECLARED_REASON_KEYS: Record<DeclaredReason, string> = { ...VARIANT_REASON_KEYS, ...JOB_FAILURE_KEYS };

const hasOwn = (record: object, key: string): boolean => Object.prototype.hasOwnProperty.call(record, key);

/** The copy key for a variant, product or plan reason; null for a literal this build does not know. */
export function declaredReasonKey(reason: string | null | undefined): string | null {
  return typeof reason === 'string' && hasOwn(VARIANT_REASON_KEYS, reason)
    ? VARIANT_REASON_KEYS[reason as keyof typeof VARIANT_REASON_KEYS]
    : null;
}

/** The copy key for a failed job's `error`; null for a free-text error (shown as it is). */
export function jobFailureKey(error: string | null | undefined): string | null {
  return typeof error === 'string' && hasOwn(JOB_FAILURE_KEYS, error)
    ? JOB_FAILURE_KEYS[error as JobFailureReason]
    : null;
}
